import React, { useState, useEffect } from 'react';

const RegistrationsTab = ({ BACKEND_URL }) => {
  const [registrations, setRegistrations] = useState([]);

  const fetchRegistrations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/registrations`);
      if (!response.ok) throw new Error('Failed to fetch registrations');
      const data = await response.json();
      setRegistrations(data);
    } catch (err) {
      console.error('Error fetching registrations:', err.message);
      setRegistrations([]);
    }
  };

  useEffect(() => { fetchRegistrations(); }, []);

  const handleDeleteRegistration = async (registrationId) => {
    if (!window.confirm('Are you sure you want to delete this registration? This action cannot be undone.')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/registrations/${registrationId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete registration');
      setRegistrations(registrations.filter(r => r.id !== registrationId));
      alert('Registration deleted successfully');
    } catch (err) {
      alert('Error deleting registration: ' + err.message);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 data-testid="registrations-title" className="text-2xl font-bold text-[#1a4d2e] mb-4">
          Quote System Registrations & Conversion Analytics
        </h2>
        <p className="text-gray-600 mb-6">
          Track user engagement from registration through quote views to booking submissions.
        </p>

        {registrations.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-600 font-semibold">Total Registrations</p>
              <p className="text-3xl font-bold text-blue-900">{registrations.length}</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-600 font-semibold">Viewed Quotes</p>
              <p className="text-3xl font-bold text-purple-900">
                {registrations.filter(r => r.quote_views_count > 0).length}
              </p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-600 font-semibold">Submitted Bookings</p>
              <p className="text-3xl font-bold text-green-900">
                {registrations.filter(r => r.has_submitted_booking).length}
              </p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-600 font-semibold">Conversion Rate</p>
              <p className="text-3xl font-bold text-orange-900">
                {registrations.length > 0
                  ? `${Math.round((registrations.filter(r => r.has_submitted_booking).length / registrations.length) * 100)}%`
                  : '0%'}
              </p>
            </div>
          </div>
        )}

        {registrations.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No registrations yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farm Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registered</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quote Views</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversion Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {registrations.map((reg) => (
                  <tr key={reg.id} className={reg.has_submitted_booking ? 'bg-green-50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{reg.customer_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{reg.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{reg.farm_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{reg.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(reg.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      <div className="flex items-center gap-2">
                        <span className={`text-lg ${reg.quote_views_count > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                          Chart
                        </span>
                        <span>{reg.quote_views_count || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-green-600 font-bold">✓</span>
                          <span className="text-xs text-gray-600">1. Registered</span>
                        </div>
                        {reg.quote_views_count > 0 ? (
                          <div className="flex items-center gap-2">
                            <span className="text-green-600 font-bold">✓</span>
                            <span className="text-xs text-gray-600">2. Quote Views ({reg.quote_views_count})</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-300 font-bold">○</span>
                            <span className="text-xs text-gray-400">2. Quote Views (0)</span>
                          </div>
                        )}
                        {reg.has_submitted_booking ? (
                          <div className="flex items-center gap-2">
                            <span className="text-green-600 font-bold">✓</span>
                            <span className="text-xs font-semibold text-green-700">3. Request Submitted</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-300 font-bold">○</span>
                            <span className="text-xs text-gray-400">3. Request Submitted</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleDeleteRegistration(reg.id)}
                        data-testid={`delete-registration-${reg.id}`}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1 rounded transition-colors"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegistrationsTab;
